import { gql, useMutation } from '@apollo/client';
import { logger } from '../utils/logger';

// Key for localStorage
const NON_CDN_URLS_KEY = 'nonCdnUrls';

// GraphQL mutation
export const ADD_URL_TO_AIRTABLE = gql`
  mutation AddUrlToAirtable($urls: [String!]!) {
    addUrlToAirtable(urls: $urls)
  }
`;

/**
 * Hook to add URLs to Airtable
 * This is used to track files that are not in the CDN
 */
export const useAddUrlToAirtable = () => {
  const [addUrlToAirtable, { loading, error }] = useMutation(ADD_URL_TO_AIRTABLE);

  /**
   * Send stored URLs to Airtable
   * This function retrieves URLs from localStorage and sends them to Airtable
   */
  const sendStoredUrlsToAirtable = async () => {
    const storedUrls = getStoredUrls();
    if (storedUrls.length === 0) {
      logger.info('No URLs to send to Airtable');
      return null;
    }

    try {
      logger.info(`Sending ${storedUrls.length} URLs to Airtable`);
      const response = await addUrlToAirtable({ variables: { urls: storedUrls } });
      logger.info('Successfully sent URLs to Airtable:', response);
      
      // Clear URLs from localStorage after successful sending
      clearStoredUrls();
      
      return response;
    } catch (err) {
      logger.error('Failed to send URLs to Airtable:', err);
      return null;
    }
  };

  return {
    sendStoredUrlsToAirtable,
    loading,
    error
  };
};

/**
 * Function to store a URL in localStorage
 * This is called from the File service when a non-CDN URL is accessed
 */
export const storeNonCdnUrl = (url: string) => {
  logger.info('Storing non-CDN URL for later sending to Airtable:', url);
  
  try {
    // Get existing URLs from localStorage
    const storedUrls = getStoredUrls();
    
    // Add new URL if it doesn't exist
    if (!storedUrls.includes(url)) {
      storedUrls.push(url);
      localStorage.setItem(NON_CDN_URLS_KEY, JSON.stringify(storedUrls));
      logger.info(`Stored URL in localStorage. Total URLs: ${storedUrls.length}`);
    }
  } catch (error) {
    logger.error('Failed to store URL in localStorage:', error);
  }
};

// Function to get stored URLs from localStorage
export const getStoredUrls = (): string[] => {
  return JSON.parse(localStorage.getItem(NON_CDN_URLS_KEY) || '[]');
};

// Function to clear stored URLs from localStorage
export const clearStoredUrls = () => {
  localStorage.removeItem(NON_CDN_URLS_KEY);
};
